import { Row, Col, Card, Button, CardBody } from 'reactstrap';
import { getDashColor, renderIcon } from '~/shared/components/icons';
import { useContext, useEffect, useState } from 'react';
import { redeemMyId } from '~/services/my-id';
import { CampaignContext, CampaignState } from '~/context/campaign';
import { MyID } from '~/types/myId';
import moment from 'moment';
import { useToasts } from 'react-toast-notifications';
import { MyIdContext } from '~/context/my-id';
import { Loading } from '~/shared/components/Loading';

const MyIDPage = () => {
  const [{ campaign }]: [CampaignState] = useContext(CampaignContext);
  const [state, setState] = useState({
    myId: null as MyID | null,
    fetching: false,
    showButton: false,
    fetchComplete: false,
  });
  const { addToast } = useToasts();
  const [myIdContext, dispatch] = useContext(MyIdContext);

  const handleClick = async () => {
    setState((prev) => ({ ...prev, fetching: true }));
    try {
      const res = await redeemMyId(campaign?.campaignDetail.campaign_id);
      setState((prev) => ({ ...prev, myId: res }));
      dispatch('set-my-id', {
        my_id_panel: true,
        my_id: res,
      });
    } catch (error) {
      addToast('Error redeeming MyID', { appearance: 'error' });
    } finally {
      setState((prev) => ({ ...prev, fetching: false }));
    }
  };

  useEffect(() => {
    if (myIdContext.my_id_panel && myIdContext.my_id) {
      setState({ ...state, myId: myIdContext.my_id, fetchComplete: true });
    } else {
      setState((prev) => ({ ...prev, showButton: true, fetchComplete: true }));
    }
  }, [
    campaign?.campaignDetail.campaign_id,
    myIdContext.my_id_panel,
    myIdContext.my_id,
    addToast,
  ]);

  const { myId, fetching, showButton, fetchComplete } = state;

  const [isCopied, setIsCopied] = useState(false);

  async function copyTextToClipboard(text) {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  // onClick handler function for the copy button
  const handleCopyClick = () => {
    // Asynchronously call copyTextToClipboard
    copyTextToClipboard(myId?.my_id_code)
      .then(() => {
        // If successful, update the isCopied state value
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  if (!fetchComplete) {
    return <Loading />;
  }

  const cta_copy = myId?.my_id_code ? (
    <p>
      Copy the code above to request your free bracelet at{' '}
      <a href='https://www.getmyid.com/helphopelive'>
        https://www.getmyid.com/helphopelive
      </a>
      .
    </p>
  ) : (
    <p>
      Click on <b>Redeem MyID</b> to reveal your exclusive code and claim your
      free bracelet today!
    </p>
  );

  return (
    <div className='container__inner'>
      <Row>
        <Col xs='12' style={{ marginBottom: '20px' }}>
          <h3>MyID</h3>
        </Col>
      </Row>

      <Row style={{ marginBottom: '24px' }}>
        <Col xs='12' md='9'>
          <Row>
            <Col xs='12' md='7' lg='9'>
              <h5>You're eligible for MyID!</h5>
              <p>
                As a member of the Help Hope Live community, <b>you qualify</b>{' '}
                for a complimentary MyID medical alert bracelet. MyID makes it
                easy to access, store, and manage your health information from
                anywhere at anytime. Learn more
              </p>
              {cta_copy}
            </Col>
            <Col xs='12' md='5' lg='3' className='mb-3 mb-md-0'>
              {showButton && !myId ? (
                <Button
                  color='primary'
                  onClick={handleClick}
                  disabled={fetching}
                >
                  {fetching ? 'Redeeming...' : 'Redeem MyID'}
                </Button>
              ) : (
                <Card
                  style={{
                    maxWidth: '100%',
                    background: getDashColor('my-id'),
                  }}
                >
                  <CardBody>
                    <p style={{ fontSize: '14px', fontWeight: 'normal' }}>
                      MYID
                    </p>

                    <h5
                      className='d-flex justify-content-between align-items-center'
                      style={{ color: getDashColor('my-id_text_color') }}
                    >
                      {myId?.my_id_code}
                      <Button
                        color='link'
                        className='p-1'
                        style={{
                          color: getDashColor('my-id_text_color'),
                        }}
                        onClick={handleCopyClick}
                      >
                        {isCopied ? (
                          <span style={{ fontSize: '11px' }}>Copied!</span>
                        ) : (
                          renderIcon('copy', getDashColor('my-id_text_color'))
                        )}
                      </Button>
                    </h5>

                    <p>
                      Redeemed{' '}
                      {moment(myId?.redemption_date).format('M/D/YYYY')}
                    </p>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </Col>
        <Col xs='12' md='3'></Col>
      </Row>

      <Row>
        <Col xs='12' md='9'>
          <h5>What is MyID?</h5>
          <p>
            MyID is the ultimate medical ID system, providing instant access to
            your health information and true peace of mind. When you're on the
            move, your health information travels with you. You can manage your
            entire medical profile and quickly update it at any time. Safely
            store critical information such as your medical conditions,
            medications, allergies, emergency contacts, and more. First
            Responders can access your medical profile in 5 seconds or less,
            telling them who you are, who to contact, and how to save your life.
          </p>
        </Col>
        <Col xs='12' md='3'></Col>
      </Row>
    </div>
  );
};

export default MyIDPage;
